<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("SIDENAV.SecurityCompanies.edit") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #446e80">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <!-- Start:: Company Name -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('SIDENAV.SecurityCompanies.name')"
            v-model.trim="data.name"
            :rules="[
              (val) => !!val || $t('VALIDATION.required'),
              (val) =>
                (val && val.length >= 3) ||
                $t('SIDENAV.SecurityCompanies.nameLengthMin'),
              (val) =>
                (val && val.length <= 50) ||
                $t('SIDENAV.SecurityCompanies.nameLengthMax'),
            ]"
            required
          />
          <!-- End:: Company Name -->

          <!-- Start:: Mobile Input -->
          <!-- <base-input
            type="tel"
            col="6"
            required
            v-model="data.phone"
            :placeholder="$t('SIDENAV.SecurityCompanies.mobile')"
          /> -->

          <base-phone-input
            col="6"
            required
            v-model="data.mobile"
            @dialCode="dialCode"
            @isoCode="isoCode"
            :placeholder="$t('SIDENAV.SecurityCompanies.mobile')"
            :defaultCountry="data.iso_code"
            :key="key"
          />
          <!-- @dialCode="dialCode"
            @isoCode="isoCode"
            :placeholder="$t('SIDENAV.SecurityCompanies.mobile')"
            :defaultCountry="data.iso_code"
            :key="key" -->
          <!-- End:: Mobile Input -->

          <!-- Start:: Email Input -->
          <base-input
            col="6"
            type="email"
            :placeholder="$t('SIDENAV.SecurityCompanies.email')"
            v-model.trim="data.email"
            required
          />
          <!-- End:: Email Input -->

          <!-- Start:: Commercial Registration Number -->
          <base-input
            col="6"
            type="text"
            :placeholder="
              $t('SIDENAV.SecurityCompanies.commercialNumberPlaceholder')
            "
            v-model.trim="data.commercial_registration_number"
            @input="validateCommercialNumber"
            :rules="[
              (val) => !!val || $t('VALIDATION.required'),
              (val) =>
                /^\d{10}$/.test(val) ||
                $t('SIDENAV.SecurityCompanies.commercialNumberValidation'),
            ]"
            required
          />
          <!-- End:: Commercial Registration Number -->

          <!-- Start:: Commercial Registration Image -->
          <base-image-upload-input
            col="12"
            identifier="commercial_registration_image"
            :preSelectedImage="data.commercial_registration_image.path"
            :placeholder="
              $t('SIDENAV.SecurityCompanies.commercialImagePlaceholder')
            "
            @selectImage="selectImage"
            :disabled="!isCommercialNumberValid"
          />
          <div v-if="!isCommercialNumberValid" class="col-12 mb-3">
            <small class="text-danger">
              {{ $t("SIDENAV.SecurityCompanies.commercialImageValidation") }}
            </small>
          </div>
          <!-- End:: Commercial Registration Image -->

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import BasePhoneInput from "@/components/formInputs/BasePhoneInput.vue";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "EditAdmin",
  components: {
    BasePhoneInput,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        commercial_registration_image: {
          path: null,
          file: null,
        },
        name: null,
        email: null,
        phone: null,
        commercial_registration_number: null,
        dial_code: null,
        iso_code: null,
      },
      isCommercialNumberValid: false,
      // End:: Data Collection To Send
    };
  },

  methods: {
    dialCode(dialCode) {
      this.data.dial_code = dialCode;
    },
    isoCode(iosCode) {
      this.data.iso_code = iosCode;
    },
    selectImage(selectedImage) {
      this.data.commercial_registration_image = selectedImage;
    },
    validateCommercialNumber() {
      // Validate that commercial registration number is exactly 10 digits
      this.isCommercialNumberValid = /^\d{10}$/.test(
        this.data.commercial_registration_number
      );
      if (
        !this.isCommercialNumberValid &&
        !this.data.commercial_registration_image.path
      ) {
        this.data.commercial_registration_image = {
          path: null,
          file: null,
        };
      }
    },
    // Start:: Get Data To Edit
    async getDataToEdit() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `providers/${this.id}`,
        });

        const company = res.data.data?.data || res.data.data;

        this.data.name = company.name;
        // Normalize phone so vue-tel-input can display it (include dial code if available)
        this.data.dial_code = company.dial_code;
        this.data.iso_code = company.code;
        const rawMobile = company.mobile || "";
        if (rawMobile.startsWith("+")) {
          this.data.phone = rawMobile;
        } else if (this.data.dial_code) {
          const dial = this.data.dial_code.startsWith("+")
            ? this.data.dial_code
            : "+" + this.data.dial_code.replace(/^\+/, "");
          this.data.phone = dial + rawMobile;
        } else {
          this.data.phone = rawMobile;
        }
        this.data.email = company.email;
        this.data.commercial_registration_number =
          company.commercial_registration_number;
        this.data.commercial_registration_image.path =
          company.commercial_registration_image;

        // Validate commercial number after loading
        this.validateCommercialNumber();
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
      }
    },
    // End:: Get Data To Edit

    // Start:: validate Form Inputs
    validateFormInputs() {
      this.isWaitingRequest = true;
      this.submitForm();
    },
    // End:: validate Form Inputs

    // Start:: Submit Form
    async submitForm() {
      const REQUEST_DATA = new FormData();

      REQUEST_DATA.append("name", this.data.name);
      REQUEST_DATA.append("email", this.data.email);
      REQUEST_DATA.append("mobile", this.data.phone);
      REQUEST_DATA.append(
        "country_code",
        this.data?.dial_code?.startsWith("+")
          ? this.data?.dial_code
          : "+" + this.data?.dial_code
      );
      REQUEST_DATA.append("iso_code", this.data.iso_code);
      REQUEST_DATA.append(
        "user_info[commercial_registration_number]",
        this.data.commercial_registration_number
      );

      if (this.data.commercial_registration_image.file) {
        REQUEST_DATA.append(
          "commercial_registration_image",
          this.data.commercial_registration_image.file
        );
      }

      REQUEST_DATA.append("_method", "PUT");

      try {
        await this.$axios({
          method: "POST",
          url: `providers/${this.id}`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.editedSuccessfully"));
        this.$router.push({ path: "/providers/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(error.response?.data?.message || error.message);
      }
    },
    // End:: Submit Form
  },

  async created() {
    // Start:: Fire Methods
    this.getDataToEdit();
    // End:: Fire Methods
  },
};
</script>
